import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, Heart, FileText, ChevronRight, X, Mic } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import Panda from "./Panda";
import { useAuth } from "../context/AuthContext";
import { useProgress } from "../context/ProgressContext";

const STEPS = [
  { icon: BookOpen, color: "#63c79a", title: "Five lessons, ten minutes each", text: "Pick your child's age and start the matching lesson. Each one has six short steps - Learn, Talk, Practice, Story, Do & Don't and a Quiz - and every step earns bamboo.", accessory: "glasses", item: "coin" },
  { icon: Heart, color: "#e5356f", title: "Invite the cheer squad", text: "Add a co-parent, then invite grandparents or mentors. They can send cheers, photos and 20-second voice notes that show up right on the lesson page.", accessory: "hat", item: "bamboo" },
  { icon: FileText, color: "#1b8bc9", title: "Watch the year add up", text: "Streaks, stickers, certificates and quiz scores roll into a yearly report card you can print, email or share - plus a year-in-review story your child will love.", accessory: "crown", item: "card" },
];

const FOLLOWER_STEPS = [
  { icon: Heart, color: "#e5356f", title: "Send a cheer in ten seconds", text: "Open the Family dashboard, find your grandchild's card and type a short note - or attach a photo, or hold the mic for a 20-second voice note. It appears on their lesson page right away.", accessory: "hat", item: "bamboo" },
  { icon: Mic, color: "#7d5fb2", title: "Leave a pep talk for a lesson", text: "Under 'Lesson pep talks', pick a lesson and record a message. It plays the moment they open that lesson - and you get an email when they tap the heart or say thanks.", accessory: "glasses", item: "coin" },
];

const key = (uid, kind) => `tycc_tour_done_${kind === "follower" ? "f_" : ""}${uid}`;

// Welcome tour: three steps for parents, two for grandparents/mentors who only follow (once per account; force with ?tour=1).
const WelcomeTour = () => {
  const { user } = useAuth();
  const { children, following } = useProgress();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const forced = new URLSearchParams(search).get("tour") === "1";
  const followsOnly = children.length === 0 && following.length > 0;
  const kind = followsOnly ? "follower" : "parent";
  const steps = followsOnly ? FOLLOWER_STEPS : STEPS;

  useEffect(() => {
    if (!user) return;
    const onPage = pathname === "/family" || pathname === "/lessons";
    if (forced || (onPage && !localStorage.getItem(key(user.id, kind)))) { setI(0); setOpen(true); }
  }, [user, pathname, forced, kind]);

  if (!user) return null;
  const finish = (go) => {
    localStorage.setItem(key(user.id, kind), "1");
    setOpen(false);
    if (forced) navigate(pathname, { replace: true });
    if (go) navigate(followsOnly ? `/family#child-${following[0].children[0]?.id || ""}` : children.length ? "/lessons" : "/panda");
  };
  const s = steps[i];
  const Icon = s.icon;
  const last = i === steps.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
      <DialogContent className="rounded-3xl p-0 overflow-hidden max-w-md" data-testid="welcome-tour" data-step={i} data-kind={kind}>
        <div className="p-8 pb-6 text-center text-white transition-colors duration-500" style={{ background: s.color }}>
          <div className="flex justify-center yir-pop" key={`p${i}`}><Panda size={120} accessory={s.accessory} scarf="#ffffff" item={s.item} animate /></div>
          <p className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest opacity-90"><Icon size={14} /> Step {i + 1} of {steps.length}</p>
        </div>
        <div className="p-6 sm:p-8 text-center" key={`t${i}`}>
          <DialogTitle className="text-2xl text-[#0c4a6e] yir-rise" style={{ fontFamily: "Literata, serif" }} data-testid="welcome-tour-title">{s.title}</DialogTitle>
          <DialogDescription className="mt-3 text-base text-gray-600 yir-rise" style={{ animationDelay: "120ms" }}>{s.text}</DialogDescription>
          <div className="mt-6 flex items-center justify-center gap-1.5">{steps.map((_, k) => <button key={k} onClick={() => setI(k)} className={`h-2 rounded-full transition-[width,background-color] ${k === i ? "w-6 bg-[#1b8bc9]" : "w-2 bg-gray-300"}`} aria-label={`Step ${k + 1}`} />)}</div>
          <div className="mt-6 flex items-center justify-between gap-3">
            <button onClick={() => finish(false)} className="text-sm font-bold text-gray-400 hover:text-gray-600 inline-flex items-center gap-1" data-testid="welcome-tour-skip"><X size={14} /> Skip</button>
            {last ? (
              <button onClick={() => finish(true)} className="btn-pill bg-[#e5356f] hover:bg-[#c92a5e] text-white inline-flex items-center gap-2 shadow" data-testid="welcome-tour-finish">{followsOnly ? "Send my first cheer" : children.length ? "Start a lesson" : "Create your first Panda"} <ChevronRight size={18} /></button>
            ) : (
              <button onClick={() => setI(i + 1)} className="btn-pill bg-[#1b8bc9] hover:bg-[#156f9f] text-white inline-flex items-center gap-2 shadow" data-testid="welcome-tour-next">Next <ChevronRight size={18} /></button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeTour;
